import React, { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { LOGOS, api } from "../lib/api";
import AdminClients from "./admin/AdminClients";
import AdminDocuments from "./admin/AdminDocuments";
import AdminImplementation from "./admin/AdminImplementation";
import AdminFeedback from "./admin/AdminFeedback";
import AdminClientView from "./admin/AdminClientView";
import AdminAccount from "./admin/AdminAccount";
import AdminBackups from "./admin/AdminBackups";
import { Users, FileText, MessageSquare, LogOut, Layers, Eye, DollarSign, Database, AlertTriangle } from "lucide-react";

const TABS = [
  { id: "clients", label: "Clients", icon: Users },
  { id: "documents", label: "Documents", icon: FileText },
  { id: "implementation", label: "Implementation", icon: Layers },
  { id: "account", label: "Billing", icon: DollarSign },
  { id: "feedback", label: "Feedback", icon: MessageSquare },
  { id: "clientview", label: "Client View", icon: Eye },
  { id: "backups", label: "Backups", icon: Database },
];

function useUnreadCounts(tab) {
  const [counts, setCounts] = useState({ documents: 0, feedback: 0 });
  useEffect(() => {
    const load = () => api.get("/admin/unread-counts").then(({ data }) => setCounts(data)).catch(() => {});
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [tab]);
  return counts;
}

function TabBadge({ count }) {
  if (!count) return null;
  return (
    <span data-testid="tab-unread-badge" className="text-[10px] font-bold px-1.5 py-0.5 rounded-full leading-none"
      style={{ backgroundColor: "#F97316", color: "#fff" }}>{count}</span>
  );
}

function DesktopNav({ tab, setTab, counts }) {
  return (
    <nav className="hidden md:flex items-center gap-1">
      {TABS.map((t) => {
        const Icon = t.icon;
        const active = tab === t.id;
        return (
          <button
            key={t.id}
            data-testid={`admin-tab-${t.id}`}
            onClick={() => setTab(t.id)}
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors"
            style={active
              ? { backgroundColor: "#0A1F44", color: "#fff" }
              : { color: "#6B7280" }}
          >
            <Icon size={16} /> {t.label}
            {t.id === "documents" && <TabBadge count={counts.documents} />}
            {t.id === "feedback" && <TabBadge count={counts.feedback} />}
          </button>
        );
      })}
    </nav>
  );
}

function MobileNav({ tab, setTab, counts }) {
  return (
    <nav className="md:hidden flex items-center gap-1 pb-2">
      {TABS.map((t) => {
        const active = tab === t.id;
        return (
          <button key={t.id} data-testid={`admin-tab-m-${t.id}`} onClick={() => setTab(t.id)}
            className="flex-1 px-3 py-2 rounded-lg text-sm font-medium flex items-center justify-center gap-1"
            style={active ? { backgroundColor: "#0A1F44", color: "#fff" } : { color: "#6B7280" }}>
            {t.label}
            {t.id === "documents" && <TabBadge count={counts.documents} />}
            {t.id === "feedback" && <TabBadge count={counts.feedback} />}
          </button>
        );
      })}
    </nav>
  );
}

function AdminHeader({ tab, setTab, logout }) {
  const counts = useUnreadCounts(tab);
  return (
    <header className="border-b sticky top-0 z-30 bg-white" style={{ borderColor: "#E5E7EB" }}>
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex items-center justify-between h-20">
          <div className="flex items-center gap-3">
            <img src={LOGOS.blueWordmark} alt="360 Safety Systems" className="h-14 w-auto" />
            <span className="text-xs font-semibold uppercase tracking-widest px-2 py-1 rounded"
              style={{ backgroundColor: "#FEF0E6", color: "#F97316" }}>Admin</span>
          </div>
          <DesktopNav tab={tab} setTab={setTab} counts={counts} />
          <button
            data-testid="admin-logout"
            onClick={logout}
            className="flex items-center gap-2 text-sm text-[#6B7280] hover:text-[#1A1A1A] transition-colors px-3 py-2 rounded-lg hover:bg-gray-50"
          >
            <LogOut size={16} /> <span className="hidden sm:inline">Log out</span>
          </button>
        </div>
        <MobileNav tab={tab} setTab={setTab} counts={counts} />
      </div>
    </header>
  );
}

function useStaleBackupBanner() {
  const [stale, setStale] = useState(null);
  useEffect(() => {
    const load = () => api.get("/admin/backups").then(({ data }) => {
      const last = data.last_success_at ? new Date(data.last_success_at) : null;
      const hrs = last ? (Date.now() - last.getTime()) / 3.6e6 : Infinity;
      setStale({ hours: hrs, last });
    }).catch(() => {});
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, []);
  return stale;
}

function StaleBackupBanner({ onGo }) {
  const stale = useStaleBackupBanner();
  if (!stale || stale.hours <= 26) return null;
  const msg = stale.last
    ? `Last successful backup was ${Math.floor(stale.hours)}h ago (${stale.last.toLocaleString()}).`
    : "No successful backup has ever completed.";
  return (
    <div data-testid="stale-backup-banner" onClick={onGo}
      className="cursor-pointer flex items-center gap-2 px-6 py-2.5 text-sm font-semibold text-white"
      style={{ backgroundColor: "#DC2626" }}>
      <AlertTriangle size={16} /> Backup overdue — {msg} Click to open Backups.
    </div>
  );
}

export default function AdminPortal() {
  const { logout } = useAuth();
  const [tab, setTab] = useState("clients");

  return (
    <div className="min-h-screen bg-white" style={{ color: "#1A1A1A" }}>
      <AdminHeader tab={tab} setTab={setTab} logout={logout} />
      <StaleBackupBanner onGo={() => setTab("backups")} />

      <main className="max-w-7xl mx-auto px-6 py-8 fade-up">
        {tab === "clients" && <AdminClients />}
        {tab === "documents" && <AdminDocuments />}
        {tab === "implementation" && <AdminImplementation />}
        {tab === "feedback" && <AdminFeedback />}
        {tab === "account" && <AdminAccount />}
        {tab === "clientview" && <AdminClientView />}
        {tab === "backups" && <AdminBackups />}
      </main>
    </div>
  );
}
